import flatMapDeep from "lodash/flatMapDeep";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import has from "lodash/has";
import isObject from "lodash/isObject";
import last from "lodash/last";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import some from "lodash/some";
import sum from "lodash/sum";
import values from "lodash/values";
import { FORM_SUBTEMPLATES_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { MultiActivityForm } from "PFApp/types/activity_form/activity_form_schema";
import { FieldError, FieldErrors } from "react-hook-form";

type SubTemplatesErrors = FieldErrors<MultiActivityForm>[typeof FORM_SUBTEMPLATES_KEY];

const isErrorObject = (obj: FieldError): boolean => has(obj, "message") && !!get(obj, "ref");
const flattenErrors = (obj: SubTemplatesErrors | FieldError): any[] => {
  if (!obj || !isObject(obj)) {
    return [];
  }

  if (isErrorObject(obj as FieldError)) {
    return [obj];
  }

  return flatMapDeep(values(obj), (value) => {
    if (Array.isArray(value)) {
      return map(value, flattenErrors);
    }
    if (typeof value === "object") {
      return flattenErrors(value as FieldError);
    }
    return [];
  });
};

export const hasMandatoryErrors = (errors?: SubTemplatesErrors): boolean =>
  some(flattenErrors(errors), ["type", "required"]);

export const countErrors = (errors?: SubTemplatesErrors): number => flattenErrors(errors).length;

const extractRoleGlobalIndex = (refName: string, formValues: MultiActivityForm): number | null => {
  const roleLocalIndexMatch = refName.match(/roles\.(\d+)\./);
  const roleLocalIndex = roleLocalIndexMatch ? parseInt(roleLocalIndexMatch[1], 10) : null;

  const subtemplateIndexMatch = refName.match(/subtemplates\.(\d+)\./);
  const subtemplateIndex = subtemplateIndexMatch ? parseInt(subtemplateIndexMatch[1], 10) : null;

  if (subtemplateIndex === null || roleLocalIndex === null) {
    return null;
  }
  const previousRolesCount = sum(
    formValues[FORM_SUBTEMPLATES_KEY].slice(0, subtemplateIndex).map(({ roles }) => roles.length)
  );

  return previousRolesCount + roleLocalIndex;
};
const extractFieldName = (refName: string): string => last(refName.split(".")) || refName;

type GroupErrorsByRoleIndexParams = {
  errors?: SubTemplatesErrors;
  formValues: MultiActivityForm;
};

export const groupErrorsByRoleIndex = ({
  errors,
  formValues
}: GroupErrorsByRoleIndexParams): Record<number, Array<{ name: string; message: string }>> => {
  const errorsByRole = groupBy(flattenErrors(errors), (error) =>
    extractRoleGlobalIndex(error.ref?.name, formValues)
  );

  return mapValues(errorsByRole, (errorsGroup) =>
    map(errorsGroup, ({ ref, message }) => ({ name: extractFieldName(ref?.name), message }))
  );
};
